import { supertypeClass, property, remote, amorphicStatic } from '@haventech/amorphic';
import { AppController } from '@hit/amorphic-types';
import _ from 'lodash';
import { PolicyDoc } from '../customer/PolicyDoc';
import { Assumptions, DocTypes } from '../static/Assumptions';
import { MetadataApi } from '@hit/metadata-module';
import { Criteria } from '@hit/metadata-module/lib/Types';
import { Utils } from '../Utils';
import { PDFPackage } from './PDFPackage';
import { PdfFormContract, PDFForm, FooterOptions } from './PDFForm';
import { PdfMappingLib } from './PdfMappingLib';

import rp from 'request-promise-native';
import * as fs from 'fs-extra';
import { SignaturePointsCollection } from './PDFFormFieldMapping';
import { PDFPackageForm } from './PDFPackageForm';
import { CommonUtils } from '../static/CommonUtils';
import { Product } from '../static/Product';
import { Customer } from '../customer/Customer';
import { CommonUtilsBackend } from '../static/CommonUtilsBackend';
import { LoggerSingleton, Category } from '../logger/LoggerSingleton';

interface CustomerInfo {
  customer: Customer;
  loggedInRole: string;
}

@supertypeClass({ toClient: false, toServer: true })
export class PdfController extends AppController {
  @property()
  fileToOpen: string;

  @property({ type: Array, getType: () => String })
  additionalDetails: Array<string>;

  @property()
  additionalDetailMaxWidth = 60;

  @property()
  additionalDetailMaxHeight = 25;

  @property()
  metadataOptions;

  @property({ type: Array, getType: () => Object })
  metadata: Array<any>;

  @property({ toServer: false })
  role: string;

  @property({ toServer: false })
  docType: string;

  @property({ toServer: false })
  applicationHTML: string;

  @property()
  customerInfo: CustomerInfo;

  @property({ toServer: false })
  applicationFooterHTML: string;

  constructor() {
    super();
  }

  static new(customerInfo: CustomerInfo) {
    return new PdfController().init(customerInfo);
  }

  init(customerInfo: CustomerInfo) {
    this.customerInfo = customerInfo;
    this.additionalDetails = [];
    return this;
  }

  @remote({ on: 'server' })
  _getMetadata(channel): Promise<any> {
    function compareTwoObjects(obj1, obj2) {
      return _.chain(obj1)
        .keys()
        .every(function (currentKey) {
          return _.has(obj2, currentKey) && _.isEqual(obj1[currentKey], obj2[currentKey]);
        })
        .value();
    }

    if (!channel) {
      LoggerSingleton.getInstance().error(
        {
          module: 'pdfController',
          function: '_getMetadata',
          category: Category.request,

          error: {
            isHumanRelated: false,
          },
        },
        `Error with pdfController: Missing Channel`
      );

      throw new Error('Error with pdfController: Missing Channel');
    }

    const opts = {
      category: Assumptions.metadataKeys.categories.AppQuestions,
      hierarchy: [],
    };

    /*
     * Compare Opts with current metadataoptions
     * if there is different, we have to reset metadata and addl_detail
     */
    if (!compareTwoObjects(opts, this.metadataOptions)) {
      this.metadataOptions = opts;
      this.additionalDetails = [];

      const category = this.metadataOptions.category || 'App_Questions';
      const hierarchy = this.metadataOptions.hierarchy ? this.metadataOptions.hierarchy : [];
      const dimensions = this.metadataOptions.dimensions ? [this.metadataOptions.dimensions] : [];

      const criteria = (
        this.metadataOptions.criteria
          ? this.metadataOptions.criteria
          : {
              key: '^((?!B00253).)*$',
            }
      ) as Criteria;

      return Promise.resolve(MetadataApi.buildMetadata(category, hierarchy, dimensions, criteria)).then(
        function (data) {
          this.metadata = data;
          return Promise.resolve(this.metadata);
        }.bind(this)
      );
    }

    return Promise.resolve(this.metadata);
  }

  @remote({ on: 'server' })
  _cutLinesForAdditionalDetail() {
    this.additionalDetails = <any>_.map(
      this.additionalDetails,
      function (detail) {
        if (detail.length > this.additionalDetailMaxWidth) {
          const regexpString = '.{1,' + this.additionalDetailMaxWidth + '}';

          return detail.match(new RegExp(regexpString, 'g'));
        } else {
          return detail;
        }
      }.bind(this)
    );

    this.additionalDetails = _.flattenDeep(this.additionalDetails);

    this.additionalDetails = _.map(this.additionalDetails, function (detail) {
      return detail.trim();
    });
  }

  @remote({ on: 'server' })
  _packageAdditionalDetail() {
    const packages = [];
    let currentPackage = [];

    for (let k = 0; k < this.additionalDetails.length; k++) {
      if (k !== 0 && k % this.additionalDetailMaxHeight === 0) {
        packages.push(_.map(currentPackage, _.clone));
        currentPackage = [];
      }

      currentPackage.push(this.additionalDetails[k]);

      if (k === this.additionalDetails.length - 1) {
        packages.push(_.map(currentPackage, _.clone));
        currentPackage = [];
      }
    }

    return packages;
  }

  @remote({ on: 'server' })
  _getCustomerFormsAsFile(role: string, applicationHTML?: string, applicatonFooter?: string): Promise<string | void> {
    // If the application is locked, use the policy docs to generate the pdf
    return Promise.resolve()
      .then(() => this._getCustomerFormsAsBuffer(role, true, false, applicationHTML, applicatonFooter))
      .then((buffer) => this.getFile(buffer));
  }

  @remote({ on: 'server' })
  _getCustomerFormsAsBuffer(
    role: string,
    flatten: boolean,
    onlyFillSignatures: boolean,
    applicationHTML?: string,
    applicationFooter?: string
  ): Promise<any> {
    // Producer or Insured or Owner or Payor...
    if (!role) {
      throw new Error('Login to use this service with a set role');
    }

    return Promise.resolve()
      .then(() => this.buildFormMappings(role, onlyFillSignatures, applicationHTML, applicationFooter))
      .then((forms) => this.fillForms(forms, true, flatten));
  }

  @remote({ on: 'server' })
  _getCustomerFormsAsBuffers(channel: string, applicationHTML?: string): Promise<any> {
    const role = this.customerInfo.loggedInRole;

    // Producer or Insured
    // or Owner or Payor
    if (!role) {
      throw new Error('Login to use this service with a set role');
    }

    return this.buildFormMappings(role, null, applicationHTML).then((forms) => this.fillForms(forms, false));
  }

  @remote({ on: 'server' })
  fillForms(forms: PdfFormContract[], merge: boolean, flatten?: boolean, typesToFilter?: Array<string>): Promise<any> {
    const channel = this.customerInfo.customer.application.primaryPolicy.getProductInfo().channel.code;
    const PDFSTREAM_URL = this.amorphic.config.nconf.get('pdfstreamUrl');
    // TODO: Remove this and move to individual functions
    const headers = {
      'Content-Type': 'application/json',
      'authorization': 'Bearer ' + Utils.getAuthTokenForPdfstream(),
      ...LoggerSingleton.getInstance()?.getHeaderContextFromLocalStorage?.(),
    };

    // ForExample: Filter only the forms of type -> A2200,Application
    if (typesToFilter) {
      forms = forms.filter((form) => typesToFilter.indexOf(form.type) > -1);
    }

    forms = forms.sort(function (a, b) {
      return a.formOrder - b.formOrder;
    });

    const options = {
      url: PDFSTREAM_URL + '/fill/' + channel + '?isFinal=' + (flatten ? 'true' : 'false'),
      body: {},
      json: true,
      method: 'POST',
      headers: headers,
    };

    if (merge) {
      options.body = {
        data: forms,
      };

      LoggerSingleton.getInstance().debug(
        {
          module: 'PdfController',
          function: 'fillForms',
          data: {
            forms: _.map(forms, 'templateName'),
          },
          category: Category.request,
        },
        `fillAndMerge pre activity`
      );
      return rp(options);
    } else {
      const fillPromises = forms.map((form) => {
        options.body = {
          data: [form],
        };
        options.url = PDFSTREAM_URL + '/fill/' + channel + '?isFinal=false';

        return rp(options)
          .then(function (body) {
            return {
              name: form.templateName,
              roles: form.roles,
              order: form.formOrder,
              body: body,
              type: form.type,
            };
          })
          .catch((err) => {
            LoggerSingleton.getInstance().error(
              {
                module: 'pdfController',
                function: '_getCustomerFormsAsBuffers',
                category: Category.request,

                error: {
                  name: err?.name,
                  message: err?.message,
                  stack: err?.stack,
                  isHumanRelated: false,
                },
              },
              'Error from PdfStream ' + err + ' Form name ' + form.templateName
            );
            throw err;
          });
      });
      return Promise.all(fillPromises);
    }
  }

  @remote({ on: 'server' })
  _fetchSignaturePoints(formName: Array<string>, channel: string): Promise<SignaturePointsCollection> {
    const result: SignaturePointsCollection = {};
    return PDFForm.persistorFetchByQuery(
      {
        name: { $in: formName },
        channel: channel,
      },
      { session: this.amorphic }
    ).then((forms) => {
      _.map(forms, (form: PDFForm) => {
        const fields = form.signatureFields();
        const structuredFields = {};
        _.forEach(fields, (field) => {
          structuredFields[field.signaturePoint.pageNumber] = {
            xPosition: field.signaturePoint.xPosition,
            yPosition: field.signaturePoint.yPosition,
            role: field.signaturePoint.role,
          };
        });
        result[form.name] = { numberOfPages: form.numberOfPages, signaturePoints: structuredFields };
      });
      return result;
    });
  }

  @remote({ on: 'server' })
  _getSignedPDFsAsBuffers(policyDocs) {
    const PDFSTREAM_URL = this.amorphic.config.nconf.get('pdfstreamUrl');

    const role = this.customerInfo.loggedInRole;

    // or Payor...
    if (!role) {
      throw new Error('Login to use this service with a set role');
    }

    // For each policy Doc, find the corresponding form and identify all
    // the signature fields
    return Promise.all([getForms(policyDocs), true])
      .then((results) => this.buildMappings.bind(this).apply(this, results))
      .then(fillForms.bind(this));

    function getForms(policyDocs) {
      const formNames = policyDocs.map(function (policyDoc) {
        return policyDoc.name;
      });

      return PDFForm.getFromPersistWithQuery(query).then(wrapForms.bind(this));

      function query(knex) {
        knex.where(function () {
          this.whereIn('name', formNames);
        });
      }

      function wrapForms(formObjs) {
        // If the retrieved forms did not match the policyDocs,
        // the form must have been deleted prematurely
        if (formObjs.length !== formNames.length) {
          const err =
            'Forms and Policy Docs do not match...' +
            '\nforms in policy docs ' +
            _.sortBy(_.map(formObjs, 'name')) +
            '\nforms in package ' +
            _.sortBy(formNames);
          const error = {
            isHumanRelated: false,
            message: err,
          };
          LoggerSingleton.getInstance().error(
            {
              module: 'pdfController',
              function: '_getSignedPDFsAsBuffers',
              error: error,
              category: Category.request,
            },
            `_getSignedPDFsAsBuffers error`
          );

          throw err;
        }

        // Wrap form objects while maintaining order with policyDocs
        return formNames.map(function (formName) {
          const form = formObjs.find(function (formObj) {
            return formObj.name === formName;
          });

          const wrappedForm: any = {};

          wrappedForm.form = form;

          return wrappedForm;
        });
      }
    }

    function fillForms(forms) {
      const token = Utils.getAuthTokenForPdfstream();
      // TODO: Remove this and move to individual functions
      const headers = {
        'Content-Type': 'application/json',
        'authorization': 'Bearer ' + token,
        ...LoggerSingleton.getInstance()?.getHeaderContextFromLocalStorage?.(),
      };

      LoggerSingleton.getInstance().debug(
        {
          module: 'PdfController',
          function: '_getSignedPDFsAsBuffers',
          category: Category.request,
        },
        'Filling forms from buffers'
      );

      const options: any = {
        url: PDFSTREAM_URL + '/fill/MM?isFinal=false',
        json: true,
        method: 'POST',
        headers: headers,
      };

      const fillPromises = forms.map(function (form, i) {
        options.body = {
          data: [policyDocs[i].doc],
          mappings: [form.mappings],
        };

        return rp(options);
      });

      return Promise.all(fillPromises);
    }
  }

  async buildFormMappings(
    role: string,
    onlyFillSignatures: boolean,
    applicationHTML?: string,
    applicationFooterHTML?: string
  ): Promise<PdfFormContract[]> {
    this.applicationHTML = applicationHTML;
    this.applicationFooterHTML = applicationFooterHTML;
    return this.filterForms(role).then((forms) => {
      return this.buildMappings(forms, onlyFillSignatures);
    });
  }

  async getPackageForms(role: string, carrier: string, docType: string): Promise<Array<string>> {
    const ownerState = this.customerInfo.customer.application.primaryPolicy.ownerPersonResidentialAddress.state;
    const formNames = [];

    if (!ownerState) {
      throw new Error('No contract state');
    }
    let filteredForms = await this.filterForms(role, carrier, docType);

    //sort forms by Order
    filteredForms = filteredForms.sort((a, b) => {
      return a.formInx - b.formInx;
    });

    filteredForms.forEach((packageForm) => formNames.push(packageForm.form.name));
    return formNames;
  }

  async filterForms(role: string, carrier?: string, docType?: string): Promise<Array<PDFPackageForm>> {
    const ownerState = this.customerInfo.customer.application.primaryPolicy.ownerPersonResidentialAddress.state;

    if (!ownerState) {
      throw new Error('No contract state');
    }

    const self = this;
    const query = {
      state: ownerState,
      docType: docType ? docType : this.docType,
    };

    if (carrier) {
      query['carrier'] = carrier;
    }
    return PDFPackage.getFromPersistWithQuery(query, { session: this.amorphic }).then(async (packages) => {
      if (packages.length > 0) {
        const currentPackage = packages[0];

        // filter out forms by role before fetching mapping
        let filteredForms = this.filterFormsByRole(currentPackage.forms, role);

        // filter out forms by conditional logic
        filteredForms = await this.filterFormsByCondition(filteredForms);
        if (!filteredForms.length) {
          throw new Error('No Forms to display');
        }

        return filteredForms;
      } else {
        throw new Error('No package for owner state');
      }
    });
  }

  private filterFormsByRole(forms, role) {
    return _.filter(forms, (form) => {
      return form.isDisplayedForRole(role);
    });
  }

  async filterFormsByCondition(forms) {
    const resultForms = [];
    for (const form of forms) {
      const entity = form.getConditionEntity();
      const property = form.getConditionFunction();

      if (!entity) {
        resultForms.push(form);
        continue;
      }

      const condition = await this.evalValue(this.customerInfo, entity, property, false, 'Boolean');

      if (condition) {
        resultForms.push(form);
      }
    }

    return resultForms;
  }

  async buildMappings(forms: Array<PDFPackageForm>, onlyFillSignatures: boolean): Promise<Array<PdfFormContract>> {
    forms = forms.sort(function (a, b) {
      return a.formInx - b.formInx;
    });
    // Go thru each form and create the form mappings
    const formObjectArr: Array<PdfFormContract> = [];
    let formObject, fields;

    const footerHTMLOptions: FooterOptions = {
      format: 'Letter',
      margin: {
        top: '0.50in',
        bottom: '0.50in',
        left: '0.25in',
        right: '0.25in',
      },
      displayHeaderFooter: true,
      headerTemplate: '<div></div>',
      footerTemplate: this.applicationFooterHTML,
    };
    if (this.isAppPacket() && this.applicationHTML) {
      formObjectArr.push({
        templateName: CommonUtils.getApplicationFormName(this.customerInfo.customer.application.primaryPolicy),
        html: this.applicationHTML,
        formOrder: 0,
        roles: _.values(PDFForm.UserRoles_e),
        formType: 'htmlFile',
        type: Assumptions.policyDocTypes.A2200,
        usePuppeteer: false,
        options: null,
      });
    }

    for (const form of forms) {
      formObject = {
        templateName: form.form.name,
        formOrder: forms.indexOf(form),
        roles: form.rolesAllowedToView,
        formType: 'pdfTemplate',
        type: form.form.type,
      };

      fields = form.form.fields;

      //If a form does not have any field, use pdfRetrieve which will not do any mapping .
      if (fields && fields.length <= 0) {
        formObject.formType = 'pdfRetrieve';
      }

      formObject.mappings = await this._processFormMappings(fields, onlyFillSignatures);
      if (this.isAppPacket() && this.applicationHTML) {
        formObject.formOrder = formObject.formOrder + 1;
      }
      formObjectArr.push(formObject);
    }

    return formObjectArr;
  }

  async _processFormMappings(fields, onlyFillSignatures, fullDetail?) {
    const formMappings = {};
    const applyFields = fields.filter((field) => {
      // If only signatures fields should be filled, check if the current field is a
      // signature field. If not include it.
      return !onlyFillSignatures ? true : field.signatureField;
    });

    for (const field of applyFields) {
      let propVal;

      if (field.fieldLabel !== 'Remarks') {
        if (!field.options || field.options.length === 0) {
          propVal = await this.evalValue(
            this.customerInfo,
            field.entity,
            field.property,
            field.isAsync,
            field.applyFunc
          );

          formMappings[field.fieldLabel] = propVal !== null ? propVal : '';
        } else {
          // Must be checkboxes or radios
          propVal = String(
            await this.evalValue(this.customerInfo, field.entity, field.property, field.isAsync, field.applyFunc)
          );

          const options = field.options;

          options.forEach(function (option) {
            const value = option['value'];

            if (propVal && value === propVal) {
              formMappings[field.fieldLabel] = option['key'];
            }
          });
        }
      } else {
        formMappings[field.fieldLabel] = fullDetail;
      }
    }

    return formMappings;
  }

  async evalValue(context, entity: string, property: string, isAsync?: boolean, applyFunc?) {
    //the following variables is setting the context to execute the promise or eval the function.
    const customer = context.customer;
    let evalRes;
    if (entity && property) {
      try {
        if (property.indexOf('()') > -1) {
          // If property is a function call - we remove parenthesis to format property accordingly to what lodash's result expects
          property = property.replace(/[()]/g, '');
        }

        const evalExp = `customer.${entity} && customer.${entity}.${property}`;
        evalRes = _.result(customer, `${entity}.${property}`);

        if (isAsync) {
          // To split the two entity, application and primary policy
          const entityContext = entity.split('.');

          if (entityContext[1]) {
            evalRes = await customer[entityContext[0]][entityContext[1]][property]();
          } else {
            evalRes = await customer[entityContext[0]][property]();
          }
        }

        if (applyFunc && applyFunc.match('EVAL_')) {
          evalRes = _.invoke(PdfMappingLib, `PdfMappingLib.${applyFunc}.func`, evalRes, evalExp);
        } else if (applyFunc && applyFunc === 'Boolean') {
          evalRes = Boolean(evalRes);
        } else if (applyFunc && applyFunc !== 'None') {
          evalRes = _.invoke(PdfMappingLib, `PdfMappingLib.${applyFunc}.func`, evalRes, evalExp);
        }
        if (evalRes !== null && typeof evalRes === 'object') {
          throw new Error('Expected simple type, received "' + typeof evalRes + '": ' + evalExp);
        }
      } catch (e) {
        LoggerSingleton.getInstance().warn(
          {
            module: 'pdfController',
            function: 'evalValue',
            category: Category.request,
          },
          `Error in evalValue ${e}`
        );
        evalRes = '';
      }
    }

    return evalRes;
  }

  getFile(buffer: string): Promise<string | void> {
    this.fileToOpen = '';

    const pdfBuff = Buffer.from(buffer, 'base64');
    const path = CommonUtilsBackend.getWritableFilePath(''); // Docker only allows specific writeable folders
    const tempFileName = path + 'temp_' + Utils.generateUuid() + '.pdf';

    return fs
      .stat(path)
      .catch(
        function createPdfDirIfNoneExists(err) {
          if (err.cause.code !== 'ENOENT') {
            throw err;
          }

          LoggerSingleton.getInstance().info(
            {
              module: 'PdfController',
              function: 'getFile',
              data: {
                path: path,
              },
              category: Category.request,
            },
            'Path does not exist, creating directory'
          );

          return fs.mkdirs(path);
        }.bind(this)
      )
      .then(function writeTempFile() {
        return fs.writeFile(tempFileName, pdfBuff);
      })
      .then(() => {
        this.fileToOpen = tempFileName;
        return tempFileName;
      })
      .catch((err) => {
        LoggerSingleton.getInstance().error(
          {
            module: 'PdfController',
            function: 'getFile',
            category: Category.request,
            error: {
              name: err?.name,
              message: err?.message,
              stack: err?.stack,
              isHumanRelated: false,
            },
          },
          'Error occurred writing file'
        );

        return 'Error occurred writing file';
      });
  }

  _getLockedPDFAsBuffer(role) {
    const policy = this.customerInfo.customer.application.primaryPolicy;
    const user = role || this.customerInfo.loggedInRole;

    if (!user) {
      throw new Error('Login to use this service with a set role');
    }

    const headers = {
      'Content-Type': 'application/json',
      'authorization': 'Bearer ' + Utils.getAuthTokenForPdfstream(),
      ...LoggerSingleton.getInstance()?.getHeaderContextFromLocalStorage?.(),
    };

    return PolicyDoc.getFromPersistWithQuery({
      $and: [
        {
          policyId: {
            $eq: policy._id,
          },
        },
        {
          uploadedBy: {
            $eq: 'Workflow',
          },
        },
      ],
    })
      .then(getBuffersAndMappings)
      .then(merge.bind(this));

    function getBuffersAndMappings(docs) {
      const mappings = [];

      // Order the forms by their order, filter by incoming role and push an empty
      // mapping set for each form
      const buffers = docs
        .sort(function (a, b) {
          if (a.order > b.order) {
            return 1;
          } else {
            return a.order < b.order ? -1 : 0;
          }
        })
        .filter(function (doc) {
          return doc.roles.indexOf(role) >= 0;
        })
        .map(function (doc) {
          mappings.push({});

          return doc.docContent;
        });

      return {
        buffers: buffers,
        mappings: mappings,
      };
    }

    function merge(map) {
      LoggerSingleton.getInstance().debug(
        {
          module: 'PDFController',
          function: 'merge()',
          data: {
            arrSize: map.length,
          },
          category: Category.request,
        },
        'Merging PDFs'
      );

      const PDFSTREAM_URL = this.amorphic.config.nconf.get('pdfstreamUrl');

      const body = {
        data: map.buffers,
        mappings: map.mappings,
      };

      const options = {
        // flatten the damn thing
        url: PDFSTREAM_URL + '/fill/MM?isFinal=true',
        json: true,
        method: 'POST',
        headers: headers,
        body: body,
      };

      return rp(options);
    }
  }

  getPdfStreamUrl() {
    return this.amorphic.config.nconf.get('pdfstreamUrl');
  }

  getHeaders() {
    const token = Utils.getAuthTokenForPdfstream();
    const headers = {
      'Content-Type': 'application/json',
      'authorization': 'Bearer ' + token,
      ...LoggerSingleton.getInstance()?.getHeaderContextFromLocalStorage?.(),
    };
    return headers;
  }

  private isAppPacket(): boolean {
    return this.docType && this.docType.includes(DocTypes.appPacket);
  }
}
